import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Buttons, Chip, FlexContainer, Icon, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import StorageService from 'Services/storage';
import { history } from 'Store';
import { pathNameSelector, routeStateSelector } from 'Store/modules/router/selectors';
import useTranslation from 'Hooks/useTranslation';
import BannerQuiz from 'Containers/Datagathering/components/BannerQuiz';
import CustomTransition from 'Components/CustomTransition';
import { lottiesPaths } from 'Constants/lottiesPaths';
import { getButtonMode, QUIZ_CONFIG } from './utils';
import * as Styled from './styles';

const STORAGE_KEY = 'quiz_correct_answers';
const CURRENT_QUIZ_KEY = 'current_quiz';

const Quiz = (): JSX.Element => {
  const { t } = useTranslation(locizeNS.DG);
  const isPreviousYears = useSelector(routeStateSelector);
  const [option, setOption] = useState<number>();
  const [hasAnswered, setHasAnswered] = useState(false);
  const [answersCorrects, setAnswersCorrects] = useState<number>(
    Number(StorageService.getItem(STORAGE_KEY) || 0)
  );
  const route = useSelector(pathNameSelector);
  const url = route.split('/').pop();
  const QUIZ_QUESTION = QUIZ_CONFIG[url];

  useEffect(() => {
    setOption(undefined);
    setHasAnswered(false);
  }, [url]);

  const handleOptionSelect = (selectedOption: number): void => {
    if (hasAnswered) {
      return;
    }
    setOption(selectedOption);
    setHasAnswered(true);
    if (selectedOption === QUIZ_QUESTION.correctAnswer) {
      const newCorrectAnswers = answersCorrects + 1;
      StorageService.setItem(STORAGE_KEY, newCorrectAnswers.toString());
      setAnswersCorrects(newCorrectAnswers);
    }
  };

  const handleClick = (quizRoute: string): void => {
    history.replace(quizRoute, {
      products: isPreviousYears?.products ?? {},
    });
    StorageService.setItem(CURRENT_QUIZ_KEY, quizRoute);
  };

  const tContext = isPreviousYears?.products?.isPreviousYear ? 'PreviousYears' : '';

  return (
    <Styled.QuizContent direction="column" fullWidth justify="space-between">
      <CustomTransition start customStyles={{ height: '70%' }}>
        <FlexContainer align="center" direction="column" fullHeight>
          <Styled.QuizContainer direction="column" align="center" justify="space-between">
            {option === QUIZ_QUESTION.correctAnswer && (
              <Styled.KiteLottie options={{ path: lottiesPaths.CONFETI }} />
            )}
            <Styled.ContentContainer direction="column" gap="dimension_24" align="center" fullWidth>
              <FlexContainer justify="space-between" fullWidth>
                {QUIZ_QUESTION.isBonus ? (
                  <Chip size="S" type="cold_line">
                    {t('Quiz.bonus')}
                  </Chip>
                ) : (
                  <Text type="paragraphs" size="XS_L_S" color="grey">
                    {t('Quiz.steps.count', {
                      step: QUIZ_QUESTION.step,
                    })}
                  </Text>
                )}
                <FlexContainer align="center" gap="dimension_04">
                  <Icon icon="StarS" color="default" />
                  <Text type="paragraphs" size="XS_L_S" color="dark">
                    {t('Quiz.steps.corrects', {
                      answersCorrects,
                    })}
                  </Text>
                </FlexContainer>
              </FlexContainer>
              <Text type="headline" size="XS_L_S" color="dark" align="center">
                {t(`Quiz.${QUIZ_QUESTION.contextKey}.question`, {
                  context: tContext,
                })}
              </Text>
              <Text type="paragraphs" size="XS_M_S" color="grey">
                {t(`Quiz.${QUIZ_QUESTION.contextKey}.subtitle`)}
              </Text>
              <Styled.AnswersContainer direction="column" gap="dimension_24">
                {Array.from({ length: QUIZ_QUESTION.totalQuestions }).map((_, index: number) => (
                  <Buttons.Classic
                    key={`answer-${index + 1}`}
                    mode={getButtonMode(
                      index + 1,
                      hasAnswered,
                      QUIZ_QUESTION.correctAnswer,
                      option
                    )}
                    size="m"
                    fullWidth
                    onClick={() => handleOptionSelect(index + 1)}
                    label={t(`Quiz.answer.${QUIZ_QUESTION.contextKey}.${index + 1}`, {
                      context: tContext,
                    })}
                  />
                ))}
                {hasAnswered && option && (
                  <BannerQuiz
                    option={option}
                    correctAnswer={QUIZ_QUESTION.correctAnswer}
                    context={QUIZ_QUESTION.contextKey}
                    tContext={tContext}
                  />
                )}
              </Styled.AnswersContainer>
            </Styled.ContentContainer>
          </Styled.QuizContainer>
        </FlexContainer>
      </CustomTransition>
      <Styled.ButtonContainer direction="column" align="center" justify="center">
        <Buttons.Classic
          mode="primary"
          size="m"
          label={t('Quiz.next.button')}
          disabled={!hasAnswered}
          onClick={() => handleClick(QUIZ_QUESTION.nextRoute)}
        />
      </Styled.ButtonContainer>
    </Styled.QuizContent>
  );
};

export default Quiz;
